import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { ENDPOINTS } from "../../../constants/ApiEndPoints";
import { properties } from "../../../util/Properties";
import { getService, postService } from "../../../services/ApiServices";
import {
  getCurrentTime,
  removeSessionStorage,
  setSessionStorage,
} from "../../../util/UtilMethods";
import {
  MESSAGE_TYPE,
  CHAT_APP,
  RESPONSE_TYPE,
  SESSION_STORAGE_KEYS,
} from "../../../constants/constant";
import { clearFormSlice } from "../FormSlice/formSlice";

const initialState = {
  isLoading: false,
  queueStateData: {},
  startChatData: {},
  receiveMessageData: {},
  receiveMessageList: [],
  isTypingOn: false,
  sendMessageData: {},
  exitChatData: {},
  participantId: "",
  isAgentTyping: false,
  isSessionOpen: true,
  surveyDetails: {},
  intervalId: "",
};

export const queueState = createAsyncThunk(
  "service/queueState",
  async (requestBody, thunkAPI) => {
    try {
      const response = await postService(
        `${ENDPOINTS.QUEUE_URL}?p_auth=${properties.authToken}`,
        requestBody
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue("something went wrong");
    }
  }
);

export const startChat = createAsyncThunk(
  "service/startChat",
  async (requestBody, thunkAPI) => {
    try {
      const response = await postService(
        `${ENDPOINTS.START_URL}?p_auth=${properties.authToken}`,
        requestBody
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue("something went wrong");
    }
  }
);

export const getPoll = createAsyncThunk(
  "service/getPoll",
  async (participantId, thunkAPI) => {
    try {
      const response = await getService(
        `${ENDPOINTS.POLL_URL}?participantID=${participantId}&p_auth=${properties.authToken}`
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue("something went wrong");
    }
  }
);

export const checkTypeState = createAsyncThunk(
  "service/getTypeState",
  async (requestBody, thunkAPI) => {
    try {
      const response = await postService(
        `${ENDPOINTS.TYPE_STATE_URL}?participantID=${requestBody.participantId}&p_auth=${properties.authToken}`,
        requestBody
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue("something went wrong");
    }
  }
);

export const sendMessages = createAsyncThunk(
  "service/sendMessages",
  async (requestBody, thunkAPI) => {
    try {
      const response = await postService(
        `${ENDPOINTS.SEND_MESSAGE_URL}?participantID=${requestBody.participantId}&p_auth=${properties.authToken}`,
        requestBody.payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue("something went wrong");
    }
  }
);

export const exitChat = createAsyncThunk(
  "service/exitChat",
  async (participantId, thunkAPI) => {
    try {
      const response = await postService(
        `${ENDPOINTS.EXIT_URL}?participantID=${participantId}&p_auth=${properties.authToken}`
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue("something went wrong");
    }
  }
);

export const sendSurveyDetails = createAsyncThunk(
  "service/sendSurveyDetails",
  async (requestBody, thunkAPI) => {
    try {
      const response = await postService(
        `${ENDPOINTS.SEND_SURVEY}?p_auth=${properties.authToken}`,
        requestBody
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue("something went wrong");
    }
  }
);

const chatSlice = createSlice({
  name: "chatSlice",
  initialState,
  reducers: {
    addChatData: (state, { payload }) => {
      state.receiveMessageList = payload;
    },
    setPaticipantId: (state, { payload }) => {
      state.participantId = payload;
    },
    setGetPollIntervalId: (state, {payload}) => {
      state.intervalId = payload;
    },
    clearChatSlice: (state, action) => {
      state.isLoading = false;
      state.queueStateData = {};
      state.startChatData = {};
      state.receiveMessageData = {};
      state.receiveMessageList = [];
      state.isTypingOn = false;
      state.sendMessageData = {};
      state.exitChatData = {};
      state.participantId = "";
      state.isSessionOpen = true;
      removeSessionStorage(SESSION_STORAGE_KEYS.CHAT_LIST);
      removeSessionStorage(SESSION_STORAGE_KEYS.PARTICIPANT_ID);
      removeSessionStorage(SESSION_STORAGE_KEYS.SURVEY_DETAILS);
    },
    clearSurveyDetails: (state) => {
      state.surveyDetails = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(queueState.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(queueState.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.queueStateData = payload;
      })
      .addCase(queueState.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(startChat.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(startChat.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.startChatData = payload;
        if (payload?.chat?.participantID) {
          state.participantId = payload?.chat?.participantID;
          setSessionStorage(
            SESSION_STORAGE_KEYS.PARTICIPANT_ID,
            payload.chat.participantID
          );
        }
      })
      .addCase(startChat.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getPoll.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPoll.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.receiveMessageData = payload;
        if (RESPONSE_TYPE.SUCCESS === payload.chat.status.type) {
          const updatedMessagesList = [];
          if (payload.chat?.events?.length > 0) {
            payload.chat.events
              .filter(
                (items) =>
                  CHAT_APP.VALUE in items &&
                  (CHAT_APP.TEXT === items?.type ||
                    MESSAGE_TYPE.TYPING_INDICATOR === items?.type)
              )
              .map((element) => {
                if (MESSAGE_TYPE.TYPING_INDICATOR === element.type) {
                  state.isAgentTyping = true;
                } else if (CHAT_APP.TEXT) {
                  state.isAgentTyping = false;
                  const updatedMessage = {
                    participantType: element.participantType,
                    value: element.value,
                    name: element.displayName,
                    time: getCurrentTime(),
                  };
                  updatedMessagesList.push(updatedMessage);
                }
                return false;
              });
            state.receiveMessageList =
              state.receiveMessageList.concat(updatedMessagesList);
            setSessionStorage(
              SESSION_STORAGE_KEYS.CHAT_LIST,
              JSON.stringify(state.receiveMessageList)
            );
          }
        } else {
          clearFormSlice();
          clearChatSlice();
          state.isSessionOpen = false;
        }
      })
      .addCase(getPoll.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(checkTypeState.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkTypeState.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.isTypingOn = payload;
      })
      .addCase(checkTypeState.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(sendMessages.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendMessages.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.sendMessageData = payload;
      })
      .addCase(sendMessages.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(exitChat.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(exitChat.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.exitChatData = payload;
      })
      .addCase(exitChat.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(sendSurveyDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendSurveyDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.surveyDetails = payload;
      })
      .addCase(sendSurveyDetails.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { addChatData, setPaticipantId, clearChatSlice, clearSurveyDetails, setGetPollIntervalId } =
  chatSlice.actions;
export default chatSlice.reducer;
